import {
  Offer,
  Plan,
  Product,
  ProductLine,
  ProductPriceUnit,
  ProductType,
  Session,
  useBillingInformationsByOrga,
  useCountryPriceTiers,
  useRecruitment,
} from 'queries';
import { getGroupProducts, useRelevantPlanProducts } from 'lib/planAndOffer';
import { useMemo } from 'react';
import { compareProducts } from 'lib/products';
import { shouldApplyVatToBillingInfo } from 'lib/billing';

export const applyDurationToPrice = (price: number, duration: number) => {
  return Math.ceil((price * duration) / 6000) * 100;
};

export interface PriceTotals {
  total: number;
  totalNoTax: number;
  taxes: number;
}

export interface ProductWithCountryGroup {
  product?: Product;
  countryPriceGroupId?: string;
}

export interface PriceProductLine {
  product: Product;
  quantity: number;
  unitPrice: number;
}

export interface WithTiers {
  product?: Product;
  countryPriceGroupId?: string;
}

export type LazyProduct = Product | { product?: Product };
export type LazyProductArray = LazyProduct[];

export const toProductArr = (arr?: LazyProductArray): Product[] => {
  const res = [] as Product[];
  if (!arr?.length) {
    return res;
  }
  for (const p of arr) {
    if ('product' in p && p.product) {
      res.push(p.product);
      continue;
    }
    if ('type' in p) {
      res.push(p as Product);
    }
  }
  return res;
};

export const filterByType = (
  products: LazyProductArray | undefined,
  type: ProductType,
): Product[] => toProductArr(products).filter((p) => p.type === type);

export const getUnitProductPrice = (
  product: Product,
  duration: number = 60,
  incentive?: number,
) => {
  switch (product.priceUnit) {
    case ProductPriceUnit.EUR_FOR_ONE_HOUR_DURATION:
      return applyDurationToPrice(product.price, duration);

    case ProductPriceUnit.INCENTIVE_PERCENT:
      if (incentive === undefined) {
        throw new Error('Missing incentive to compute price');
      }
      return Math.ceil((product.price / 100) * incentive);

    default:
      return product.price;
  }
};

export const getProductPrice = (
  product: Product,
  numberOfParticipant: number,
  duration: number = 60,
  incentive?: number,
) => {
  const factor = product.pricePerParticipant ? numberOfParticipant : 1;
  return getUnitProductPrice(product, duration, incentive) * factor;
};

export const sumProducts = (
  products?: LazyProductArray,
  numberOfParticipant: number = 1,
  duration: number = 60,
  incentive?: number,
) => {
  let sum = 0;
  for (const p of toProductArr(products)) {
    sum += getProductPrice(p, numberOfParticipant, duration, incentive);
  }
  return sum;
};

export const computeIncentive = (
  products?: LazyProductArray,
  duration: number = 60,
) => {
  return sumProducts(
    filterByType(products, ProductType.INCENTIVE),
    1,
    duration,
  );
};

export const computeFees = (
  products?: LazyProductArray,
  duration: number = 60,
  incentive?: number,
  includePerParticipantFeature?: boolean,
) => {
  if (includePerParticipantFeature) {
    return sumProducts(
      toProductArr(products).filter(
        (p) => !!p.pricePerParticipant && p.type !== ProductType.INCENTIVE,
      ),
      1,
      duration,
      incentive,
    );
  }
  return sumProducts(
    filterByType(products, ProductType.PARTICIPANT_FEE),
    1,
    duration,
    incentive,
  );
};

export const computeOptions = (
  products?: LazyProductArray,
  numberOfParticipant: number | false = 1,
  duration: number = 60,
  incentive?: number,
) => {
  if (numberOfParticipant === false) {
    return sumProducts(
      toProductArr(products).filter((p) => !p.pricePerParticipant),
      1,
      duration,
      incentive,
    );
  }
  return sumProducts(
    filterByType(products, ProductType.FEATURE),
    numberOfParticipant,
    duration,
    incentive,
  );
};

export const NO_PRICE_GROUP = 'NO_PRICE_GROUP';
export const getProductsByCountryGroups = (
  products?: WithTiers[],
): Record<string, Product[]> => {
  const res = {} as Record<string, Product[]>;
  if (!products) {
    return res;
  }

  for (const p of products) {
    if (!p.product) {
      continue;
    }

    const group = p.countryPriceGroupId || NO_PRICE_GROUP;

    if (!res[group]) {
      res[group] = [];
    }
    res[group].push(p.product);
  }
  return res;
};

export const getProductLinesWithGroupQuantity = (
  products: WithTiers[] | undefined,
  groupQuantity: Record<string, number>,
  duration: number = 60,
): PriceProductLine[] => {
  const productsByGroup = getProductsByCountryGroups(products);
  const quantityTotal = Object.values(groupQuantity).reduce(
    (acc, num) => acc + num,
    0,
  );

  const lines: PriceProductLine[] = [];

  for (const group in productsByGroup) {
    const incentive = computeIncentive(productsByGroup[group], duration);

    for (const product of productsByGroup[group]) {
      let quantity =
        group === NO_PRICE_GROUP ? quantityTotal : groupQuantity[group];

      if (!product.pricePerParticipant) {
        quantity = 1;
      }
      if (!quantity) {
        continue;
      }

      const unitPrice = getUnitProductPrice(product, duration, incentive);
      lines.push({
        product,
        unitPrice,
        quantity,
      });
    }
  }

  return dedupLines(lines);
};

export const dedupLines = (lines: PriceProductLine[]): PriceProductLine[] => {
  const res: PriceProductLine[] = [];

  for (const line of lines) {
    const existing = res.find(
      (l) => l.product.id === line.product.id && l.unitPrice === line.unitPrice,
    );
    if (existing) {
      existing.quantity += line.quantity;
      continue;
    }
    res.push(line);
  }
  return res;
};

export const convertOrderProductLine = (
  lines?: ProductLine[],
  duration: number = 60,
): PriceProductLine[] => {
  if (!lines) {
    return [];
  }
  const incentive = computeIncentive(lines, duration);

  return lines
    .filter((l) => !!l.product)
    .map((line) => {
      const product = line.product!;
      return {
        product: product,
        quantity: line.quantity,
        unitPrice: getUnitProductPrice(
          product,
          line.duration || duration,
          incentive,
        ),
      };
    });
};

export const sumProductLines = (lines: PriceProductLine[]): number => {
  return lines.reduce((acc, l) => {
    return acc + l.quantity * l.unitPrice;
  }, 0);
};

export enum PriceDisplayMethod {
  FIXED = 'FIXED',
  PER_PARTICIPANT = 'PER_PARTICIPANT',
}
/**
 * Compute the price for an option or a support offer
 * Where there is only one kind of products
 * Return the price and how you should display the price
 */
export const getSimpleOfferPrice = (lazyProducts?: LazyProductArray) => {
  let priceDisplayMethod = PriceDisplayMethod.FIXED;
  const products = toProductArr(lazyProducts);
  if (
    products.some((p) => p.priceUnit !== ProductPriceUnit.EUR && p.price !== 0)
  ) {
    throw new Error(
      'This offer require more advanced price computation function',
    );
  }

  if (products.some((p) => p.pricePerParticipant)) {
    priceDisplayMethod = PriceDisplayMethod.PER_PARTICIPANT;
  }

  return {
    amount: sumProducts(products, 1),
    priceDisplayMethod,
  };
};

export const usePlanPriceTotal = (
  session?: Session,
  plan?: Plan,
  applyVat?: boolean,
): PriceTotals => {
  const organizationId = session?.organizationId;
  const duration = session?.duration ?? 60;
  const numberOfParticipants = session?.numberOfParticipantRequired ?? 1;

  const { data: billingInformation } =
    useBillingInformationsByOrga(organizationId);
  const { data: recruitment } = useRecruitment(session?.recruitmentId);

  const countryKeys = recruitment?.countryKeys;
  const countryPriceGroupIds = useCountryPriceTiers(countryKeys);

  const products = useRelevantPlanProducts(plan?.id, session?.id, countryKeys);

  const relevantProducts = useMemo(() => {
    return products
      .filter((p: ProductWithCountryGroup) => !!p.product && p.product!.title)
      .sort((a: ProductWithCountryGroup, b: ProductWithCountryGroup) =>
        compareProducts(a.product!, b.product!),
      );
  }, [products]);

  const nonEmptyCountryPriceGroups = useMemo(
    (): string[] =>
      countryPriceGroupIds?.length ? countryPriceGroupIds : [NO_PRICE_GROUP],
    [countryPriceGroupIds],
  );

  const productsByGroup = useMemo(() => {
    const res: Record<string, ProductWithCountryGroup[]> = {};
    for (const group of nonEmptyCountryPriceGroups) {
      res[group] = getGroupProducts(relevantProducts, group);
    }
    return res;
  }, [nonEmptyCountryPriceGroups, relevantProducts]);

  const totals = useMemo(() => {
    return nonEmptyCountryPriceGroups.map((group) => {
      const products = productsByGroup[group];
      const incentive = computeIncentive(products, duration);
      return sumProducts(products, numberOfParticipants, duration, incentive);
    });
  }, [
    nonEmptyCountryPriceGroups,
    productsByGroup,
    duration,
    numberOfParticipants,
  ]);

  const totalNoTax = totals.length > 0 ? Math.max(...totals) : 0;

  const shouldApplyVat = useMemo(() => {
    if (applyVat === false) {
      return false;
    }
    return shouldApplyVatToBillingInfo(billingInformation);
  }, [applyVat, billingInformation]);

  const taxes = useMemo(() => {
    if (!shouldApplyVat) {
      return 0;
    }
    return totalNoTax * 0.2;
  }, [shouldApplyVat, totalNoTax]);

  const total = totalNoTax + taxes;

  return {
    total,
    totalNoTax,
    taxes,
  };
};

export const useOfferPriceTotal = (
  offer?: Offer,
  organizationId?: string,
  applyVat?: boolean,
): PriceTotals => {
  const { data: billingInformation } =
    useBillingInformationsByOrga(organizationId);

  const totalNoTax = useMemo(() => {
    if (!offer?.products) {
      return 0;
    }

    return sumProducts(offer.products);
  }, [offer]);

  const shouldApplyVat = useMemo(() => {
    if (applyVat === false) {
      return false;
    }
    return shouldApplyVatToBillingInfo(billingInformation);
  }, [applyVat, billingInformation]);

  const taxes = useMemo(() => {
    if (!shouldApplyVat) {
      return 0;
    }
    return totalNoTax * 0.2;
  }, [shouldApplyVat, totalNoTax]);

  const total = totalNoTax + taxes;

  return {
    total,
    totalNoTax,
    taxes,
  };
};
